import { useCallback, useMemo, useState } from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { AnimatedCollapse } from '@reflex/ui/components/shared/AnimatedCollapse';
import { cn } from '@reflex/ui/lib/utils';
import { formatDurationPrecise, formatElapsedMs } from '@reflex/ui/lib/format';
import type { AxonEvent } from '@/lib/types';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { useElapsedTime } from '@/hooks/ui/useElapsedTime';
import { toErrorMessage } from '@reflex/plugin-api/errors';
import { StreamBubbleContainer } from '@reflex/ui/components/stream/StreamBubbleContainer';
import { api } from '@/lib/api';
import { buildStepsFromEvents, parseSetupEvents } from './agent-setup-progress/parse-events';
import { SetupStepRow } from './agent-setup-progress/SetupStepRow';
import type { LogsState, SetupGroup, SetupStepInfo } from './agent-setup-progress/types';

const GROUP_ORDER: SetupGroup[] = ['provisioning', 'initializing', 'daemons'];
const GROUP_LABELS: Record<SetupGroup, string> = {
  provisioning: 'Provisioning devbox',
  initializing: 'Initializing devbox',
  daemons: 'Daemons',
};

interface AgentSetupProgressProps {
  setupEvents: AxonEvent[];
  isComplete: boolean;
  /** When provided, renders a "Logs" expander next to each step. */
  agentId?: string;
}

export function AgentSetupProgress({ setupEvents, isComplete, agentId }: AgentSetupProgressProps) {
  const [userOverride, setUserOverride] = useState<boolean | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [expandedMcpServers, setExpandedMcpServers] = useState<Set<string>>(new Set());
  const [logsByStep, setLogsByStep] = useState<Record<string, LogsState>>({});

  const fetchStepLog = useCallback(
    (stepId: string) => {
      if (!agentId) return;
      setLogsByStep((prev) => {
        if (prev[stepId]) return prev;
        return { ...prev, [stepId]: { loading: true, content: null, error: null } };
      });

      void (async () => {
        try {
          const text = await api.getServiceLog(agentId, stepId);
          setLogsByStep((prev) => ({
            ...prev,
            [stepId]: { loading: false, content: text, error: null },
          }));
        } catch (err) {
          setLogsByStep((prev) => ({
            ...prev,
            [stepId]: {
              loading: false,
              content: null,
              error: toErrorMessage(err),
            },
          }));
        }
      })();
    },
    [agentId],
  );

  const toggleStep = useCallback(
    (stepId: string, hasSubRows: boolean) => {
      setExpandedSteps((prev) => {
        const next = new Set(prev);
        if (next.has(stepId)) next.delete(stepId);
        else next.add(stepId);
        return next;
      });
      if (!hasSubRows) {
        // Plain steps fetch eagerly so opening the row reveals the log.
        fetchStepLog(stepId);
      }
    },
    [fetchStepLog],
  );

  const toggleMcpServer = useCallback(
    (stepId: string, serverName: string) => {
      const key = `${stepId}::${serverName}`;
      setExpandedMcpServers((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      // Lazy-load the parent step's log on first server expansion; per-server
      // sections are sliced from that single fetch.
      fetchStepLog(stepId);
    },
    [fetchStepLog],
  );

  const parsed = useMemo(() => parseSetupEvents(setupEvents), [setupEvents]);
  const { expectedSteps, completedSteps, inProgressSteps } = parsed;
  const steps = useMemo(() => buildStepsFromEvents(parsed), [parsed]);

  const groupedSteps = useMemo(() => {
    const groups: Record<SetupGroup, SetupStepInfo[]> = {
      provisioning: [],
      initializing: [],
      daemons: [],
    };
    for (const step of steps) groups[step.group ?? 'initializing'].push(step);
    return groups;
  }, [steps]);

  // Provisioning (Runloop infra) and initializing (our blocking launch
  // commands) spans both ride on the `creating_devbox` step. The headline
  // rollup is blocking-only, so daemons — which run detached after launch —
  // are deliberately excluded.
  const provisioningStep = useMemo(() => steps.find((s) => s.id === 'creating_devbox'), [steps]);
  const provisioningMs = provisioningStep?.durationMs;
  const initializingMs = provisioningStep?.initializingMs;
  const blockingRollupMs =
    provisioningMs != null || initializingMs != null
      ? (provisioningMs ?? 0) + (initializingMs ?? 0)
      : null;

  // Setup is "complete" once the gating (non-daemon) steps resolve — the
  // agent launches when the devbox is ready, not when detached daemons
  // finish. Daemons keep timing in the background and resolve their own rows.
  const gatingSteps = useMemo(
    () => expectedSteps.filter((s) => (s.group ?? 'initializing') !== 'daemons'),
    [expectedSteps],
  );
  const gatingDone =
    gatingSteps.length > 0 && gatingSteps.every((s) => completedSteps.includes(s.id));
  const effectiveComplete = isComplete || gatingDone;
  const isOpen = userOverride ?? !effectiveComplete;

  const startTimestamp = setupEvents.length > 0 ? setupEvents[0].timestamp : null;
  const endTimestamp =
    effectiveComplete && setupEvents.length > 0
      ? setupEvents[setupEvents.length - 1].timestamp
      : null;
  const elapsedMs = useElapsedTime(startTimestamp, {
    endMs: endTimestamp,
    stopped: effectiveComplete,
  });
  const elapsed = elapsedMs != null && elapsedMs > 0 ? formatElapsedMs(elapsedMs) : null;
  // Once complete, the headline reports the blocking-only rollup (excludes
  // detached daemons); while still running it ticks the live wall-clock.
  const headlineTime =
    effectiveComplete && blockingRollupMs != null
      ? formatDurationPrecise(blockingRollupMs)
      : elapsed;

  const activeStep = useMemo(() => {
    if (effectiveComplete) return null;
    if (expectedSteps.length > 0) {
      return expectedSteps.find((s) => !completedSteps.includes(s.id))?.id ?? null;
    }
    return null;
  }, [effectiveComplete, expectedSteps, completedSteps]);

  if (steps.length === 0) return null;

  const completedCount = completedSteps.length;
  const totalCount = steps.length;
  const interactive = agentId != null;

  return (
    <StreamBubbleContainer className="my-0 py-1">
      <div
        data-testid="agent-setup-progress"
        data-complete={effectiveComplete ? 'true' : undefined}
        data-state={isOpen ? 'open' : 'closed'}
      >
        <button
          type="button"
          data-testid="agent-setup-progress-toggle"
          data-state={isOpen ? 'open' : 'closed'}
          aria-expanded={isOpen}
          onClick={() => setUserOverride(!isOpen)}
          className="flex w-full items-center gap-2 rounded-lg border border-border bg-card/50 px-4 py-2.5 text-xs transition-colors hover:bg-card/80"
        >
          <ChevronRight
            className={cn(
              'h-3 w-3 shrink-0 text-muted-foreground transition-transform duration-200',
              isOpen && 'rotate-90',
            )}
          />
          {effectiveComplete ? (
            <>
              <Check className="h-3 w-3 shrink-0 text-status-success" />
              <span className="text-muted-foreground">Setup complete</span>
              <span className="text-muted-foreground">
                ({totalCount} steps{headlineTime ? ` · ${headlineTime}` : ''})
              </span>
            </>
          ) : (
            <>
              <Spinner size="xs" className="text-status-info" />
              <span className="text-foreground">Setting up&hellip;</span>
              <span className="text-muted-foreground">
                ({completedCount}/{totalCount}
                {elapsed ? ` · ${elapsed}` : ''})
              </span>
            </>
          )}
        </button>

        <AnimatedCollapse open={isOpen}>
          <div className="mt-1 rounded-lg border border-border bg-card/50 px-4 py-3">
            <div className="flex flex-col gap-2">
              {GROUP_ORDER.map((group) => {
                const groupSteps = groupedSteps[group];
                if (groupSteps.length === 0) return null;

                const renderRow = (step: SetupStepInfo) => (
                  <SetupStepRow
                    key={step.id}
                    step={step}
                    isDone={completedSteps.includes(step.id)}
                    isActive={inProgressSteps.includes(step.id) || step.id === activeStep}
                    isExpanded={expandedSteps.has(step.id)}
                    expandedMcpServers={expandedMcpServers}
                    logs={logsByStep[step.id]}
                    interactive={interactive}
                    onToggleStep={toggleStep}
                    onToggleMcpServer={toggleMcpServer}
                  />
                );

                // Provisioning is a single top-level line (the devbox row
                // carries the id + Runloop time); the other groups render a
                // section header with their steps indented beneath it.
                if (group === 'provisioning') {
                  // The provisioning span is Runloop-owned with no per-step log,
                  // so the row renders non-interactively (no expand chevron)
                  // rather than opening an empty log panel.
                  return (
                    <div key={group} className="flex flex-col gap-1">
                      {groupSteps.map((step) => (
                        <SetupStepRow
                          key={step.id}
                          step={step}
                          isDone={completedSteps.includes(step.id)}
                          isActive={inProgressSteps.includes(step.id) || step.id === activeStep}
                          isExpanded={expandedSteps.has(step.id)}
                          expandedMcpServers={expandedMcpServers}
                          logs={logsByStep[step.id]}
                          interactive={false}
                          onToggleStep={toggleStep}
                          onToggleMcpServer={toggleMcpServer}
                        />
                      ))}
                    </div>
                  );
                }

                const groupTime = group === 'initializing' ? initializingMs : undefined;
                return (
                  <div key={group} className="flex flex-col gap-1">
                    <div
                      data-testid={`setup-group-${group}`}
                      className="flex items-center gap-2 text-xs text-muted-foreground/70"
                    >
                      <span>{GROUP_LABELS[group]}</span>
                      {groupTime != null && (
                        <span className="ml-auto shrink-0 text-3xs tabular-nums text-muted-foreground/70">
                          {formatDurationPrecise(groupTime)}
                        </span>
                      )}
                    </div>
                    <div className="ml-5 flex flex-col gap-1">{groupSteps.map(renderRow)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </AnimatedCollapse>
      </div>
    </StreamBubbleContainer>
  );
}
